import React, { useCallback, type ComponentProps } from 'react';
import type { ResponseCluster } from '@mentimeter/http-clients';
import { GroupCard } from '@mentimeter/slides-presentation-ui/groups/group-card';
import type { GroupShapeMap } from '@mentimeter/slides-presentation-ui/groups/group-shapes';
import { Grid } from '@mentimeter/slides-presentation-ui/grid';
import { useTranslations } from 'next-intl';
import { usePresentationContext } from '../../../../legacy-context/presentationContext';
import { UNGROUPED_CLUSTER_ID } from '../../groups';
import { useOpenSlideState } from '../utils/useOpenSlideState';
import { UngroupedGroupCard } from './UngroupedGroupCard';

interface GroupsOverviewCardsProps
  extends Pick<
    ComponentProps<typeof GroupCard>,
    'upvotes' | 'shouldShowVotes'
  > {
  groups: ResponseCluster[];
  maxGroupVoteCount?: number;
  groupShapeMap: GroupShapeMap;
}

export function GroupsOverviewCards({
  groups = [],
  shouldShowVotes = false,
  maxGroupVoteCount,
  groupShapeMap,
}: GroupsOverviewCardsProps) {
  const [, setSlideState] = useOpenSlideState();
  const { useConfig } = usePresentationContext();
  const { readonly } = useConfig();
  const translate = useTranslations('presenting.legacy');

  const handleOpenDetailView = useCallback(
    (groupId: string) => {
      setSlideState({ activeClusterId: groupId });
    },
    [setSlideState],
  );

  return (
    <Grid as="section" numberOfColumns={2} shouldAnimate>
      {groups.map((group) => {
        const isUngroupedGroup = group.id === UNGROUPED_CLUSTER_ID;

        if (isUngroupedGroup) {
          const groupTag = !shouldShowVotes
            ? translate('open.uncategorized_label')
            : undefined;

          return (
            <UngroupedGroupCard
              key={group.id}
              title={
                group.label
                  ? group.label
                  : translate('clustering.untitled_group')
              }
              groupId={group.id}
              groupShape={groupShapeMap.get(group.id)}
              tag={groupTag}
              onClick={handleOpenDetailView}
              upvotes={group.total_upvotes}
              shouldShowVotes={shouldShowVotes}
              responses={group.responses}
              isTopVoted={group.total_upvotes === maxGroupVoteCount}
              disabled={readonly}
            />
          );
        }

        return (
          <GroupCard
            key={group.id}
            title={
              group.label ? group.label : translate('clustering.untitled_group')
            }
            groupId={group.id}
            groupShape={groupShapeMap.get(group.id)}
            onClick={handleOpenDetailView}
            upvotes={group.total_upvotes}
            shouldShowVotes={shouldShowVotes}
            responses={group.responses.map((response) => ({
              text: response.text,
            }))}
            isTopVoted={group.total_upvotes === maxGroupVoteCount}
            disabled={readonly}
          />
        );
      })}
    </Grid>
  );
}
